import React, { useState } from 'react';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { CalendarDaysIcon, CheckIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { classNames } from '../utils/classNames';

// A period of days, from a first date to a last date, for example the nights of a report table. The anatomy is the
// anatomy of MonthRangePicker: the button shows the period ("28 Oct 2026 – 25 Jan 2027"), and the name of the preset
// when the period is one ("Next 90 nights · 28 Oct 2026 – 25 Jan 2027"; below the sm width the dates only, so a phone does not
// cut them). It opens a panel: the presets of the caller on
// the left; on the right the From and To dates and two months of days.
// - A click on a preset applies it and closes the panel.
// - On the days, the first click picks one end and the second click the other end (the order does not matter). Between
//   the two clicks, the days under the pointer show the period, and From / To show the two ends. The panel closes after
//   the second click. A second click on the same day gives a period of one day.
// - Days before `min` or after `max` are disabled. The arrows ("Month before", "Month after": not "Next month", which is
//   a usual preset name) move both months by one month.
// - `maxMonths`: after the first click, a day that gives a longer period is disabled, and the hint says the limit. The
//   rule is the rule of java.time: the last date is before the first date plus `maxMonths` months (31 Aug + 6 months =
//   28 Feb, so the last date can be 27 Feb).
// - `today` marks that day with a ring.
// - `variant` 'bare': no ring and no shadow, 32 px high, for a picker inside a frame that already has an edge
//   (a CaptionGroup). 'button' (the default): the white toolbar button with a ring.
// Below the sm width the presets sit above the days, and the panel shows one month. The weeks start on Monday.
// The Component knows nothing about the data. Dates are YYYY-MM-DD.

export type DateRange = {
	// YYYY-MM-DD, from <= to.
	from: string,
	to: string,
};

export type DateRangePreset = {
	id: string,
	label: string,
	range: DateRange,
};

type Props = {
	value: DateRange,
	onChange: (range: DateRange) => void,
	presets?: DateRangePreset[],
	// The first and the last date that the user can pick, YYYY-MM-DD.
	min?: string,
	max?: string,
	// The longest period, in months, that a pick on the days can give (see the rule above).
	maxMonths?: number,
	// The date of today, YYYY-MM-DD: a ring on that day.
	today?: string,
	variant?: 'button' | 'bare',
	// The accessible name of the button ("Nights of the page").
	label: string,
	disabled?: boolean,
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const WEEKDAYS_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const pad = (n: number) => String(n).padStart(2, '0');
const keyOf = (year: number, month: number, day: number) => `${year}-${pad(month + 1)}-${pad(day)}`;
const daysIn = (year: number, month: number) => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
// 0 = Monday … 6 = Sunday.
const weekdayOf = (year: number, month: number, day: number) => (new Date(Date.UTC(year, month, day)).getUTCDay() + 6) % 7;
const partsOf = (key: string) => [Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, Number(key.slice(8, 10))];

// The date plus n months; the day stays, or becomes the last day of a shorter month (as java.time plusMonths).
export const addMonths = (key: string, n: number): string => {
	const [year, month, day] = partsOf(key);
	const total = year * 12 + month + n;
	const y = Math.floor(total / 12), m = total % 12;
	return keyOf(y, m, Math.min(day, daysIn(y, m)));
};

const dayName = (key: string) => { const [y, m, d] = partsOf(key); return `${d} ${MONTHS[m]} ${y}`; };

// "28 Oct 2026" · "5 – 20 Oct 2026" · "28 Oct – 27 Nov 2026" · "28 Oct 2026 – 25 Jan 2027".
export const formatDateRange = (range: DateRange): string => {
	if (range.from === range.to) return dayName(range.from);
	const [fy, fm, fd] = partsOf(range.from);
	const [ty] = partsOf(range.to);
	if (fy !== ty) return `${dayName(range.from)} – ${dayName(range.to)}`;
	if (range.from.slice(0, 7) === range.to.slice(0, 7)) return `${fd} – ${dayName(range.to)}`;
	return `${fd} ${MONTHS[fm]} – ${dayName(range.to)}`;
};

const sameRange = (a: DateRange, b: DateRange) => a.from === b.from && a.to === b.to;
const ordered = (a: string, b: string): DateRange => a <= b ? { from: a, to: b } : { from: b, to: a };

const ARROW = 'rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent';

const End: React.FunctionComponent<{ caption: string, value: string | null, active: boolean }> = ({ caption, value, active }) =>
	<div className={classNames('min-w-0 flex-1 rounded-md px-2.5 py-1.5 ring-1 ring-inset', active ? 'ring-2 ring-blue-600' : 'ring-gray-200')}>
		<p className="text-xs text-gray-500">{caption}</p>
		<p className={classNames('truncate text-sm tabular-nums', value === null ? 'text-gray-400' : 'font-semibold text-gray-900')}>{value ?? 'Pick a date'}</p>
	</div>;

const Days: React.FunctionComponent<{
	value: DateRange, min?: string, max?: string, maxMonths?: number, today?: string, onPick: (range: DateRange) => void,
}> = ({ value, min, max, maxMonths, today, onPick }) => {
	// The left month: the month of the first date of the period.
	const [start, setStart] = useState(() => { const [y, m] = partsOf(value.from); return y * 12 + m; });
	const [first, setFirst] = useState<string | null>(null);
	const [hover, setHover] = useState<string | null>(null);
	const shown: DateRange = first === null ? value : ordered(first, hover ?? first);
	const minMonth = min === undefined ? null : partsOf(min)[0] * 12 + partsOf(min)[1];
	const maxMonth = max === undefined ? null : partsOf(max)[0] * 12 + partsOf(max)[1];
	const tooLong = (a: string, b: string) => { const r = ordered(a, b); return maxMonths !== undefined && r.to >= addMonths(r.from, maxMonths); };
	const off = (key: string) => (min !== undefined && key < min) || (max !== undefined && key > max) || (first !== null && tooLong(first, key));

	const pick = (key: string) => {
		if (first === null) {
			setFirst(key);
			return;
		}
		onPick(ordered(first, key));
	};

	// The left month has the previous arrow and, below the sm width (one month), also the next arrow.
	const month = (index: number, left: boolean) => {
		const y = Math.floor(index / 12), m = index % 12;
		const blanks = weekdayOf(y, m, 1);
		const canBack = minMonth === null || start > minMonth;
		const canForward = maxMonth === null || start + 1 < maxMonth;
		const canForwardOne = maxMonth === null || start < maxMonth;
		const next = (enabled: boolean) => <button type="button" aria-label="Month after" disabled={!enabled} onClick={() => setStart(start + 1)} className={ARROW}>
			<ChevronRightIcon aria-hidden="true" className="h-5 w-5" />
		</button>;
		return <div key={index} className={classNames('px-3 pt-3', left ? '' : 'hidden sm:block')}>
			<div className="mb-2 flex items-center justify-between">
				{left
					? <button type="button" aria-label="Month before" disabled={!canBack} onClick={() => setStart(start - 1)} className={ARROW}>
						<ChevronLeftIcon aria-hidden="true" className="h-5 w-5" />
					</button>
					: <span aria-hidden="true" className="h-7 w-7" />}
				<p className="text-sm font-semibold text-gray-900" aria-live="polite">{MONTHS_LONG[m]} {y}</p>
				{left
					? <span className="sm:hidden">{next(canForwardOne)}</span>
					: next(canForward)}
				{left && <span aria-hidden="true" className="hidden h-7 w-7 sm:block" />}
			</div>
			<div className="grid grid-cols-7 gap-y-1" onMouseLeave={() => setHover(null)}>
				{WEEKDAYS.map((w, i) => <abbr key={w} title={WEEKDAYS_LONG[i]} className="flex h-6 items-center justify-center text-xs font-medium text-gray-400 no-underline">{w}</abbr>)}
				{Array.from({ length: blanks }, (_, i) => <span key={`blank-${i}`} aria-hidden="true" />)}
				{Array.from({ length: daysIn(y, m) }, (_, i) => {
					const key = keyOf(y, m, i + 1);
					const end = key === shown.from || key === shown.to;
					const inside = key > shown.from && key < shown.to;
					const disabled = off(key);
					const isToday = key === today;
					return <button key={key} type="button" disabled={disabled} aria-pressed={end || inside}
						aria-label={`${WEEKDAYS_LONG[weekdayOf(y, m, i + 1)]} ${i + 1} ${MONTHS_LONG[m]} ${y}${isToday ? ', today' : ''}`}
						onClick={() => pick(key)} onMouseEnter={() => setHover(key)} onFocus={() => setHover(key)}
						className={classNames(
							'mx-auto h-9 w-9 rounded-md text-sm tabular-nums focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600',
							disabled ? 'cursor-not-allowed text-gray-300'
								: (end ? 'bg-blue-600 font-semibold text-white'
									: (inside ? 'bg-blue-50 font-medium text-blue-700 hover:bg-blue-100' : 'text-gray-700 hover:bg-gray-100')),
							isToday && !end ? 'ring-1 ring-inset ring-blue-600' : '',
						)}>
						{i + 1}
					</button>;
				})}
			</div>
		</div>;
	};

	const limit = maxMonths === undefined ? '' : ` (up to ${maxMonths} months)`;
	return <div>
		<div className="flex gap-x-2 px-3 pt-3">
			<End caption="From" value={first === null ? dayName(value.from) : dayName(shown.from)} active={first === null} />
			<End caption="To" value={first === null ? dayName(value.to) : (hover === null || hover === first ? null : dayName(shown.to))} active={first !== null} />
		</div>
		<div className="flex flex-col sm:flex-row sm:divide-x sm:divide-gray-100">
			<div className="sm:w-72">{month(start, true)}</div>
			<div className="sm:w-72">{month(start + 1, false)}</div>
		</div>
		<p className="px-3 pb-3 pt-2 text-xs text-gray-500" aria-live="polite">
			{first === null ? `Pick the first date, then the last${limit}.` : `From ${dayName(first)}: now pick the last date${limit}.`}
		</p>
	</div>;
};

const DateRangePicker: React.FunctionComponent<Props> = ({
	value, onChange, presets = [], min, max, maxMonths, today, variant = 'button', label, disabled = false,
}) => {
	const active = presets.find(p => sameRange(p.range, value));
	return <Popover className="relative max-w-full">
		<PopoverButton aria-label={`${label}: ${active === undefined ? '' : `${active.label}, `}${formatDateRange(value)}`} disabled={disabled}
			className={classNames(
				'inline-flex max-w-full items-center gap-x-2 rounded-md bg-white text-sm font-medium text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:cursor-not-allowed disabled:opacity-50',
				variant === 'bare' ? 'h-8 px-2 hover:bg-gray-50' : 'h-9 px-3 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
			)}>
			<CalendarDaysIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-gray-400" />
			<span className="truncate">
				{/* Below the sm width the dates alone fill the button, so the name of the preset shows from sm (the label keeps it). */}
				{active !== undefined && <span className="hidden font-normal text-gray-500 sm:inline">{active.label} · </span>}
				{formatDateRange(value)}
			</span>
			<ChevronDownIcon aria-hidden="true" className="-mr-1 h-4 w-4 shrink-0 text-gray-400" />
		</PopoverButton>
		<PopoverPanel anchor={{ to: 'bottom start', gap: 8, padding: 16 }}
			className="z-50 w-80 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-none sm:w-auto">
			{({ close }) => <div className="flex flex-col sm:flex-row">
				{presets.length > 0 && <ul className="grid grid-cols-2 gap-1 border-b border-gray-100 p-2 sm:block sm:w-44 sm:border-b-0 sm:border-r">
					{presets.map(preset => {
						const on = active?.id === preset.id;
						return <li key={preset.id}>
							<button type="button" aria-pressed={on} onClick={() => { onChange(preset.range); close(); }}
								className={classNames('flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-left text-sm focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600',
									on ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700 hover:bg-gray-50')}>
								{preset.label}
								{on && <CheckIcon aria-hidden="true" className="h-4 w-4 shrink-0" />}
							</button>
						</li>;
					})}
				</ul>}
				<Days value={value} min={min} max={max} maxMonths={maxMonths} today={today} onPick={range => { onChange(range); close(); }} />
			</div>}
		</PopoverPanel>
	</Popover>;
};

export default DateRangePicker;
